/**
 * TNCY-9 §c — `useOwnerMemberIIs` merged owner/member list for the Inspection
 * Instructions page.
 *
 * Mirrors the Contracts `useOwnerMemberContracts` (TNCY-6b) but for the II
 * list, which the BE extended with `scope=member` + ownership metadata
 * (`ownership`/`ownerTenantName`/`role`) on `IiListItem` in
 * projects/v1@2.6.0 (TNCY-8). On `scope=member` the BE composes AND
 * de-duplicates the two cross-tenant sources server-side — the assigned-out
 * direction (`assigned_to_tenant_id`, SB6-II-03 / XT-D1) UNION the
 * grant-exposed direction (`project_contractor` / `reporting_subcontractor`,
 * XT-D22) — so a row that is both appears exactly once. The FE therefore just
 * renders the single deduped list with one ownership badge (AC-2); it does
 * NOT re-derive the union.
 *
 * It fires BOTH scopes and merges them into one table via the shared
 * `mergeOwnerMember` primitive:
 *
 *   - `scope=own`     — the caller's own IIs, SERVER-paginated.
 *   - `scope=member`  — the cross-tenant set, loaded in FULL but BOUNDED.
 *
 * The client-side dedupe in `mergeOwnerMember` (by `inspectionInstructionId`)
 * is a defensive second line — the BE already deduped the member set, and an
 * own row can't also be a member row.
 *
 * Own-only fast path: when there are no member rows the hook returns the own
 * query's items + its server `total` verbatim, so a tenant with no
 * cross-tenant visibility sees exactly the pre-TNCY-9 list.
 */
import { useMemo } from "react";
import { mergeOwnerMember, MEMBER_PAGE_SIZE } from "@aeriosiq/ownership";
import { useInspectionInstructionsListQuery } from "./useInspectionInstructions.js";
import type {
  IiListItem,
  IiSortDir,
  IiSortKey,
  ListInspectionInstructionsParams,
} from "./types.js";

export interface OwnerMemberIIsResult {
  rows: IiListItem[];
  total: number;
  hasMemberRows: boolean;
  memberTruncated: boolean;
  isPending: boolean;
  isError: boolean;
}

/** R0627-06 — record-origin lens for the Inspection Instructions list.
 *  `all` = the merged own + shared view (default); `owned` = only the
 *  caller's own rows; `shared` = only rows shared in by other tenants. */
export type IiOrigin = "all" | "owned" | "shared";

function compareBy(
  key: IiSortKey,
  dir: IiSortDir,
): (a: IiListItem, b: IiListItem) => number {
  const sign = dir === "asc" ? 1 : -1;
  return (a, b) => {
    let av: number | string;
    let bv: number | string;
    switch (key) {
      case "instructionCode":
        av = a.instructionCode;
        bv = b.instructionCode;
        break;
      case "projectName":
        // projectName is nullable since 2.22.0 (orphan / field-created IIs).
        av = a.projectName ?? "";
        bv = b.projectName ?? "";
        break;
      case "siteName":
        av = a.siteName ?? "";
        bv = b.siteName ?? "";
        break;
      case "assetName":
        av = a.assetName ?? "";
        bv = b.assetName ?? "";
        break;
      case "pilotName":
        av = a.pilotName ?? "";
        bv = b.pilotName ?? "";
        break;
      case "assignedCompany":
        av = a.assignedCompanyName ?? "";
        bv = b.assignedCompanyName ?? "";
        break;
      case "plannedDate":
        av = a.plannedDate ?? "";
        bv = b.plannedDate ?? "";
        break;
      case "state":
        av = a.state;
        bv = b.state;
        break;
      default:
        av = a.instructionCode;
        bv = b.instructionCode;
    }
    if (typeof av === "number" && typeof bv === "number") {
      return (av - bv) * sign;
    }
    return String(av).localeCompare(String(bv)) * sign;
  };
}

/**
 * @param params  the page's list params (page/pageSize/sort/filters). The
 *                hook derives the own + member queries from it; do NOT pass
 *                `scope` — it's set internally.
 * @param origin  R0627-06 record-origin lens. `all` merges both scopes
 *                (default). `owned` returns ONLY the own scope (server-
 *                paginated, correct total). `shared` returns ONLY the member
 *                scope, server-paginated by the page's own page/pageSize so
 *                counts + pagination stay correct.
 */
export function useOwnerMemberIIs(
  params: ListInspectionInstructionsParams,
  origin: IiOrigin = "all",
): OwnerMemberIIsResult {
  const ownParams = useMemo<ListInspectionInstructionsParams>(
    () => ({ ...params, scope: "own" }),
    [params],
  );
  const own = useInspectionInstructionsListQuery(ownParams);

  // For the `shared` lens the member scope IS the list, so paginate it with
  // the page's own page/pageSize. For `all`/`owned` it's a bounded full load
  // that the merge consumes (and `owned` ignores).
  const memberParams = useMemo<ListInspectionInstructionsParams>(
    () =>
      origin === "shared"
        ? { ...params, scope: "member" }
        : { ...params, scope: "member", page: 1, pageSize: MEMBER_PAGE_SIZE },
    [params, origin],
  );
  const member = useInspectionInstructionsListQuery(memberParams);

  const sortKey = params.sortBy ?? "instructionCode";
  const sortDir = params.sortDir ?? "desc";

  return useMemo<OwnerMemberIIsResult>(() => {
    if (origin === "owned") {
      const items = own.data?.items ?? [];
      return {
        rows: items,
        total: own.data?.total ?? 0,
        hasMemberRows: false,
        memberTruncated: false,
        isPending: own.isPending,
        isError: own.isError,
      };
    }
    if (origin === "shared") {
      const items = member.data?.items ?? [];
      return {
        rows: items,
        total: member.data?.total ?? items.length,
        hasMemberRows: items.length > 0,
        memberTruncated: false,
        isPending: member.isPending,
        isError: member.isError,
      };
    }
    const memberItems = member.data?.items ?? [];
    // Member query failing is non-fatal — own remains the spine.
    const merged = mergeOwnerMember<IiListItem>({
      ownItems: own.data?.items ?? [],
      ownTotal: own.data?.total ?? 0,
      memberItems,
      memberTotal: member.data?.total ?? memberItems.length,
      getId: (r) => r.inspectionInstructionId,
      compare: compareBy(sortKey, sortDir),
    });
    return {
      ...merged,
      isPending: own.isPending,
      isError: own.isError,
    };
  }, [
    origin,
    own.data,
    own.isPending,
    own.isError,
    member.data,
    member.isPending,
    member.isError,
    sortKey,
    sortDir,
  ]);
}
